import React, { useId, useState, useEffect, useRef } from "react";

interface NumberFieldProps {
  label: string;
  value: number | string;
  onChange: (value: number | string) => void;
  min?: number;
  max?: number;
  step?: number;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  prefix?: string;
  suffix?: string;
  helpText?: string;
  placeholder?: string;
  /** Limit digits before the decimal point (e.g. 3 → max 999). */
  maxIntegerDigits?: number;
  /** Limit digits after the decimal point (e.g. 2 → two decimal places). */
  maxDecimals?: number;
}

const NumberField: React.FC<NumberFieldProps> = ({ label, value, onChange, min, max, step, error, required = false, disabled = false, prefix, suffix, helpText, placeholder, maxIntegerDigits, maxDecimals }) => {
  const id = useId();
  const focused = useRef(false);
  const toDisplay = (v: number | string) => {
    if (typeof v === 'number' && Number.isNaN(v)) return '';
    return placeholder !== undefined && (v === 0 || v === '') ? '' : v;
  };
  const [localVal, setLocalVal] = useState<number | string>(() => toDisplay(value));
  useEffect(() => {
    if (!focused.current) setLocalVal(toDisplay(value));
  }, [value]);

  const labelSt: React.CSSProperties = { fontSize: "12px", fontWeight: 500, color: "var(--t2, #5A6178)", fontFamily: "var(--font-primary, DM Sans, sans-serif)", lineHeight: "16px" };
  const wrapSt: React.CSSProperties = { display: "flex", alignItems: "center", border: error ? "1px solid var(--red, #E5484D)" : "1px solid var(--card-border, #E2E5EC)", borderRadius: "8px", backgroundColor: disabled ? "var(--hover, #F0F1F5)" : "var(--surface, #FFFFFF)", transition: "border-color 0.15s ease, box-shadow 0.15s ease", overflow: "hidden" };
  const inputSt: React.CSSProperties = { fontSize: "13px", fontFamily: "var(--font-primary, DM Sans, sans-serif)", color: disabled ? "var(--t3, #9CA3B8)" : "var(--t1, #1A1D2E)", border: "none", paddingTop: "10px", paddingBottom: "10px", paddingLeft: prefix ? "4px" : "14px", paddingRight: suffix ? "4px" : "14px", outline: "none", width: "100%", boxSizing: "border-box" as const, backgroundColor: "transparent", cursor: disabled ? "not-allowed" : "text", MozAppearance: "textfield" as any };
  const affixSt: React.CSSProperties = { fontSize: "13px", fontWeight: 500, color: "var(--t3, #9CA3B8)", fontFamily: "var(--font-primary, DM Sans, sans-serif)", paddingTop: 0, paddingBottom: 0, paddingLeft: "2px", paddingRight: "2px", flexShrink: 0, userSelect: "none" };
  const errSt: React.CSSProperties = { fontSize: "11px", fontWeight: 500, color: "var(--red, #E5484D)", fontFamily: "var(--font-primary, DM Sans, sans-serif)", margin: 0, marginTop: "4px" };

  const handleWrapFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    focused.current = true;
    if (!error) { e.currentTarget.style.borderColor = "var(--accent, #D23A3B)"; e.currentTarget.style.boxShadow = "0 0 0 3px var(--accent-soft, rgba(210, 58, 59, 0.1))"; }
  };
  const handleWrapBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    focused.current = false;
    setLocalVal(toDisplay(value));
    if (!error) { e.currentTarget.style.borderColor = "var(--card-border, #E2E5EC)"; e.currentTarget.style.boxShadow = "none"; }
  };

  // Clamp the integer/decimal digit counts when those limits are configured.
  const clampDigits = (raw: string): string => {
    if (raw === "" || (maxIntegerDigits === undefined && maxDecimals === undefined)) return raw;
    // Only allow negative when the field permits it (min is undefined or negative)
    const allowNegative = min === undefined || min < 0;
    const negative = allowNegative && raw.startsWith("-");
    const unsigned = raw.startsWith("-") ? raw.slice(1) : raw;
    const hasDot = unsigned.includes(".");
    const [intRaw, ...rest] = unsigned.split(".");
    let intPart = intRaw;
    let decPart = rest.join("");
    if (maxIntegerDigits !== undefined) intPart = intPart.slice(0, maxIntegerDigits);
    if (maxDecimals === 0) return negative ? `-${intPart}` : intPart;
    if (maxDecimals !== undefined) decPart = decPart.slice(0, maxDecimals);
    const rebuilt = hasDot ? `${intPart}.${decPart}` : intPart;
    return negative ? `-${rebuilt}` : rebuilt;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;
    // Allow only digits, decimal point, and optionally a leading minus
    const allowNeg = min === undefined || min < 0;
    raw = allowNeg
      ? raw.replace(/[^0-9.\-]/g, '').replace(/(?!^)-/g, '')
      : raw.replace(/[^0-9.]/g, '');
    // Strip minus when field is non-negative (min ≥ 0 or min is 0)
    if (min !== undefined && min >= 0) raw = raw.replace(/-/g, '');
    const clamped = clampDigits(raw);
    // Force the DOM to reflect the clamp even when React bails out on identical state.
    if (clamped !== e.target.value) e.target.value = clamped;
    setLocalVal(clamped);
    if (clamped === "" || clamped === "-") {
      onChange("");
    } else {
      const num = Number(clamped);
      if (!Number.isNaN(num)) {
        // Enforce min: if the entered value is a complete number (not an intermediate like "1." while typing "1.5")
        // and it falls below min, block it entirely — don't update localVal or call onChange.
        if (min !== undefined && !clamped.endsWith('.') && num < min) {
          e.target.value = String(localVal);
          return;
        }
        if (max !== undefined && !clamped.endsWith('.') && num > max) {
          e.target.value = String(localVal);
          return;
        }
        onChange(num);
      }
      // incomplete intermediates like "." — keep localVal but don't overwrite parent
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
      <label htmlFor={id} style={labelSt}>
        {label}
        {required && <span style={{ color: "var(--accent, #D23A3B)", marginLeft: "2px" }}>*</span>}
      </label>
      <div style={wrapSt} onFocus={handleWrapFocus} onBlur={handleWrapBlur}>
        {prefix && <span style={{ ...affixSt, paddingLeft: "12px", paddingRight: "2px" }}>{prefix}</span>}
        <input
          id={id}
          type="text"
          inputMode={maxDecimals === 0 ? "numeric" : "decimal"}
          value={localVal}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={(e) => {
            // Block minus, e/E, + for non-negative fields; block e/E/+ always (not valid numerics)
            const blocked = ['-', '+', 'e', 'E'];
            if (blocked.includes(e.key) && (min === undefined || min >= 0)) {
              e.preventDefault();
            }
            // Block decimal point when decimals are not allowed
            if (maxDecimals === 0 && (e.key === '.' || e.key === ',')) {
              e.preventDefault();
            }
          }}
          disabled={disabled}
          required={required}
          style={inputSt}
        />
        {suffix && <span style={{ ...affixSt, paddingLeft: "2px", paddingRight: "12px" }}>{suffix}</span>}
      </div>
      {error && <p style={errSt}>{error}</p>}
      {helpText && !error && <p style={{ fontSize: "11px", color: "var(--t3, #9CA3B8)", margin: 0, marginTop: "2px" }}>{helpText}</p>}
    </div>
  );
};

export default NumberField;
